import type { UIMatch } from 'react-router';
import type { RouteHandle } from '@/routing/Router';

import { Outlet, useMatches } from 'react-router';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { CoreWrapper } from '@/shared/session/guards';
import { ToastProvider } from '@/shared/components/Toast';
import { routes } from '@/constants/Constants';
import { useUrlChecker } from '@/routing/useUrlChecker';
import ProfileDrawer from './components/ProfileDrawer';
import { SideMenuV2 as SideMenu } from './components/side-menu';
import SiteFooter from './components/SiteFooter';
import SiteFooterPublic from '@/layouts/public/components/SiteFooterPublic';
import { SiteHeader } from './components/site-header';
import { LayoutProvider, useLayoutContext } from '@/layouts/LayoutContext';
import { InviteLeaseDetailsDrawer } from '@/features/RenterInvite';

type Props = {
  children?: React.ReactNode;
};

const PageWrapperContent = ({ children }: Props) => {
  const { isPublic } = useUrlChecker();
  const preRegister = isPublic || window.location.pathname === routes.unitsOwnedEnroll;
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
  const currentRoute = matches[matches.length - 1];
  const routeIsSplitView = currentRoute?.handle?.layout === 'split-view';
  const hideFooter = currentRoute?.handle?.hideFooter === true;

  const { isSplitView: contextIsSplitView, hideFooter: contextHideFooter } = useLayoutContext();
  const isSplitView = routeIsSplitView || contextIsSplitView;
  const shouldHideFooter = hideFooter || contextHideFooter;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {preRegister ? null : <SideMenu />}
      <Box
        id="page-container"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <SiteHeader />
        <Box
          sx={{
            flex: 1,
            px: { xs: 2, md: isPublic ? 0 : 4 },
          }}
        >
          <Box sx={{ margin: { xl: '0 auto' } }}>{children}</Box>
        </Box>
        {!shouldHideFooter &&
          (isPublic ? <SiteFooterPublic /> : <SiteFooter isSplitView={isSplitView} />)}
        <ProfileDrawer />
      </Box>
    </Box>
  );
};

const InviteDrawerGate = () => {
  const { isPublic } = useUrlChecker();
  if (isPublic) return null;
  return <InviteLeaseDetailsDrawer />;
};

const PrivateLayout = ({ children }: Props) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CoreWrapper>
        <ToastProvider>
          <LayoutProvider>
            <PageWrapperContent>{children ?? <Outlet />}</PageWrapperContent>
            <InviteDrawerGate />
          </LayoutProvider>
        </ToastProvider>
      </CoreWrapper>
    </LocalizationProvider>
  );
};

export default PrivateLayout;
